'use client';

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Code2,
  Check,
  Copy,
  X,
  Sparkles,
  ExternalLink,
  Layers,
  Monitor,
  Smartphone,
  Maximize2,
} from 'lucide-react';

export interface EmbedButtonProps {
  toolSlug: string;
  categorySlug: string;
  toolName: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

type SizePreset = 'standard' | 'compact' | 'large' | 'custom';

export function EmbedButton({
  toolSlug,
  categorySlug,
  toolName,
  className = '',
  variant = 'outline',
  size = 'md',
  showLabel = true,
}: EmbedButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<SizePreset>('standard');
  const [customWidth, setCustomWidth] = useState('100%');
  const [customHeight, setCustomHeight] = useState('600');
  const [includeBorder, setIncludeBorder] = useState(true);

  const modalRef = useRef<HTMLDivElement>(null);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://bop.ink';
  const embedUrl = `${baseUrl}/${categorySlug}/${toolSlug}?embed=true`;

  // Determine current active dimensions based on preset
  const { width, height } = useMemo(() => {
    switch (selectedPreset) {
      case 'standard':
        return { width: '100%', height: '600' };
      case 'compact':
        return { width: '420', height: '560' };
      case 'large':
        return { width: '100%', height: '750' };
      case 'custom':
        return { width: customWidth, height: customHeight };
    }
  }, [selectedPreset, customWidth, customHeight]);

  // Generate clean HTML iframe embed code
  const embedCode = useMemo(() => {
    const borderStyle = includeBorder
      ? 'border:1px solid #e2e8f0; border-radius:16px; box-shadow: 0 4px 20px -2px rgba(0,0,0,0.05);'
      : 'border:0;';
    const formattedWidth = width.endsWith('%') || width.endsWith('px') ? width : `${width}px`;
    const formattedHeight = height.endsWith('%') || height.endsWith('px') ? height : `${height}px`;

    return `<iframe\n  src="${embedUrl}"\n  width="${formattedWidth}"\n  height="${formattedHeight}"\n  style="${borderStyle} overflow:hidden;"\n  title="${toolName} — BOP.INK"\n  loading="lazy"\n  allow="clipboard-read; clipboard-write"\n></iframe>`;
  }, [embedUrl, width, height, includeBorder, toolName]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2500);
  }, []);

  const handleCopyCode = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(embedCode);
      } else if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = embedCode;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setIsCopied(true);
      showToast('Đã sao chép mã nhúng HTML vào bộ nhớ tạm!');

      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2500);
    } catch {
      showToast('Không thể sao chép tự động. Vui lòng copy thủ công khung mã.');
    }
  };

  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs gap-1.5 rounded-lg',
    md: 'px-3.5 py-2 text-sm gap-2 rounded-xl',
    lg: 'px-4 py-2.5 text-base gap-2.5 rounded-xl font-medium',
  };

  const variantClasses = {
    primary:
      'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm hover:shadow transition-all',
    secondary:
      'bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 transition-colors',
    outline:
      'bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 border border-slate-200/90 shadow-2xs transition-all',
    ghost:
      'bg-transparent hover:bg-slate-100 active:bg-slate-200 text-slate-600 transition-colors',
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title="Lấy mã nhúng tiện ích vào website / blog của bạn"
        className={`inline-flex items-center justify-center font-medium select-none transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 active:scale-95 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      >
        <Code2 className="w-4 h-4 text-indigo-600" />
        {showLabel && <span>Nhúng Tiện Ích</span>}
      </button>

      {/* Modal Dialog */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity animate-in fade-in"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal Container */}
          <div
            ref={modalRef}
            className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 p-6 sm:p-8 space-y-6 z-10 animate-in zoom-in-95 duration-200 text-slate-900"
          >
            {/* Header */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-100">
              <div className="space-y-1 pr-6">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                    <Code2 className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                    Nhúng Tiện Ích Vào Website Của Bạn
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-slate-500">
                  Dán mã iframe dưới đây vào trang web, bài viết blog (WordPress, Notion, Webflow) của bạn để người dùng sử dụng tiện ích trực tiếp.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors shrink-0 cursor-pointer"
                title="Đóng (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Size Options & Presets */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Kích Thước Khung Nhúng (Iframe Size)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="chk-border"
                    checked={includeBorder}
                    onChange={(e) => setIncludeBorder(e.target.checked)}
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="chk-border" className="text-xs font-medium text-slate-600 cursor-pointer">
                    Bo góc &amp; viền mềm
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedPreset('standard')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    selectedPreset === 'standard'
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-2xs font-bold'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  <span>Chuẩn (100% x 600px)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedPreset('compact')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    selectedPreset === 'compact'
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-2xs font-bold'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  <span>Cột / Sidebar (420px)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedPreset('large')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    selectedPreset === 'large'
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-2xs font-bold'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <Maximize2 className="w-4 h-4" />
                  <span>Lớn (100% x 750px)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedPreset('custom')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    selectedPreset === 'custom'
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-2xs font-bold'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <Layers className="w-4 h-4" />
                  <span>Tùy Chỉnh</span>
                </button>
              </div>

              {selectedPreset === 'custom' && (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-2 gap-3 animate-in fade-in">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Chiều Rộng (Width)
                    </label>
                    <input
                      type="text"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      placeholder="100% hoặc 600px"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 bg-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Chiều Cao (Height)
                    </label>
                    <input
                      type="text"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      placeholder="600"
                      className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 bg-white font-mono"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Generated Code Snippet Box */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Mã Nhúng HTML (Embed Code)
                </span>
                <span className="text-[11px] text-slate-400">100% Client-Side • Phản hồi 0ms</span>
              </div>

              <div className="relative group">
                <pre className="p-4 rounded-2xl bg-slate-900 text-indigo-200 text-xs font-mono overflow-x-auto leading-relaxed border border-slate-800 selection:bg-indigo-600 selection:text-white">
                  <code>{embedCode}</code>
                </pre>

                <button
                  type="button"
                  onClick={handleCopyCode}
                  className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold shadow-md transition-all cursor-pointer ${
                    isCopied
                      ? 'bg-emerald-600 text-white scale-105'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:scale-102'
                  }`}
                >
                  {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{isCopied ? 'Đã Sao Chép' : 'Sao Chép Mã'}</span>
                </button>
              </div>
            </div>

            {/* Live URL Link & Attribution Notice */}
            <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 text-indigo-900 font-medium">
                <Sparkles className="w-4 h-4 text-indigo-600 shrink-0" />
                <span>
                  Đường dẫn nhúng trực tiếp: <code className="font-mono font-bold text-indigo-700">{embedUrl}</code>
                </span>
              </div>
              <a
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-950 font-bold underline shrink-0"
              >
                <span>Xem Thử Khung Nhúng</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Floating Toast */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 bg-slate-950 text-white text-xs sm:text-sm font-medium rounded-xl shadow-2xl border border-slate-800 animate-in slide-in-from-bottom-3">
          <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
            <Check className="w-3.5 h-3.5" />
          </div>
          <span>{toastMessage}</span>
        </div>
      )}
    </>
  );
}
